import { LATEST_DATA } from 'api-validations/stitch'

import { defaultState, DetailsTab, type MapState, MapMode } from '../core'
import { isEditorLayer, layersHaveSameIdentity } from '../data-layer-state'
import { emptyDiagnosticsSession, emptyScoutingSession } from '../mode-sessions'
import type { InField, PaneScope } from '../session'
import { Pane, type PaneTarget, type ResolvedMapAction } from './actions'

/**
 * The single writer. Every cascade the store performs lives here, so the next
 * state is coherent by construction rather than by which entry point the
 * caller happened to reach for.
 */
export const applyTransition = (prev: MapState, action: ResolvedMapAction): MapState =>
  scrubberScopedToStream(reduce(prev, action))

/**
 * The scrubber addresses a record within a stream, so it means nothing while the
 * main pane holds a plan asset, an editor, or terrain. Enforced once on the way
 * out rather than per case: the URL restores a record independently of the layer
 * it sits beside, so any transition can arrive carrying a contradictory pair.
 *
 * A structural fix would hang the cursor off the session so the pair could not
 * be built at all, at the cost of double narrowing in all four scrubber setters.
 */
const scrubberScopedToStream = (state: MapState): MapState => {
  if (state.session.kind === 'InField' && state.session.mainMap.kind === 'AsApplied') return state
  if (state.record === LATEST_DATA && state.recordSelections === null) return state
  return { ...state, record: LATEST_DATA, recordSelections: null }
}

const reduce = (prev: MapState, action: ResolvedMapAction): MapState => {
  switch (action.kind) {
    case 'SetPaneLayer':
      return setPaneLayer(prev, action.pane, action)

    case 'SetBothPanes': {
      const withMain = setMainLayer(prev, action.main)
      return setComparisonLayer(withMain, action.comparison)
    }

    case 'SetPaneScope':
      return withPaneScope(prev, action.pane, action.scope)

    case 'SetStreamGrouping': {
      const grouped: MapState = {
        ...prev,
        streamGroups: {
          ...prev.streamGroups,
          [action.pane]: {
            ...prev.streamGroups[action.pane],
            [action.scopeKey]: action.partition
          }
        }
      }
      return action.target === null ? grouped : setPaneLayer(grouped, action.pane, action.target)
    }

    case 'EnterEditor': {
      const cameFrom =
        prev.session.kind === 'InField' && !isEditorLayer(prev.session.mainMap)
          ? prev.session.mainMap
          : null
      const next = setMainLayer(prev, action)
      return { ...next, planEditorReturnTo: cameFrom ?? prev.planEditorReturnTo }
    }

    case 'DropStaleComparison': {
      if (prev.session.kind !== 'InField' || prev.session.comparisonMap === null) return prev
      return disarmPassEdit({
        ...prev,
        session: { ...prev.session, comparisonMap: null }
      })
    }

    case 'SetComparisonDisabled': {
      if (prev.session.kind !== 'InField' || prev.session.comparisonMap === null) return prev
      if (prev.session.comparisonMap.disabled === action.disabled) return prev
      return {
        ...prev,
        session: {
          ...prev.session,
          comparisonMap: { ...prev.session.comparisonMap, disabled: action.disabled }
        }
      }
    }

    case 'SetMode':
      return {
        ...prev,
        mode: action.mode,
        dataTools: idleDataTools({ state: prev, mode: action.mode, detailsTab: prev.detailsTab })
      }

    case 'SetDetailsTab':
      return {
        ...prev,
        detailsTab: action.tab,
        dataTools: idleDataTools({ state: prev, mode: prev.mode, detailsTab: action.tab })
      }

    case 'SetBrowseFilter': {
      if (prev.session.kind !== 'BrowseFields') return prev
      return { ...prev, session: { ...prev.session, filter: action.filter } }
    }

    case 'ReturnToBrowseFields':
      return {
        ...defaultState,
        session: action.session,
        legendOverrides: prev.legendOverrides
      }

    case 'SetRecordWithHistory': {
      // Landing where you already are would push a back-stack entry that goes
      // nowhere, so the identity case has to be excluded rather than optimized.
      if (prev.record === action.record) return prev
      return {
        ...prev,
        record: action.record,
        recordSelections: {
          previous: [prev.record, ...(prev.recordSelections?.previous ?? [])],
          next: []
        }
      }
    }

    case 'SetRecordWithoutHistory':
      return { ...prev, record: action.record }

    case 'GoBackRecord': {
      const history = prev.recordSelections
      if (history === null) return prev
      const [head, ...rest] = history.previous
      if (head === undefined) return prev
      return {
        ...prev,
        record: head,
        recordSelections: { previous: rest, next: [prev.record, ...history.next] }
      }
    }

    case 'GoForwardRecord': {
      const history = prev.recordSelections
      if (history === null) return prev
      const [head, ...rest] = history.next
      if (head === undefined) return prev
      return {
        ...prev,
        record: head,
        recordSelections: { previous: [prev.record, ...history.previous], next: rest }
      }
    }

    case 'ViewDiagnoseTable': {
      const moved = reduce(prev, {
        kind: 'SetPaneLayer',
        pane: Pane.Main,
        layer: action.layer,
        scope: action.scope
      })
      const atRecord = reduce(moved, { kind: 'SetRecordWithHistory', record: action.record })
      const inDiagnose = reduce(atRecord, { kind: 'SetMode', mode: MapMode.Diagnose })
      return {
        ...inDiagnose,
        diagnosticsSession: {
          ...(inDiagnose.diagnosticsSession ?? emptyDiagnosticsSession),
          focusedModules: action.focusedModules
        }
      }
    }

    case 'RestoreSession': {
      const identityHeld =
        prev.session.kind === 'InField' &&
        layersHaveSameIdentity(prev.session.mainMap, action.session.mainMap)

      const base = identityHeld ? prev : clearMainScopedState(prev)
      return disarmPassEdit({
        ...base,
        session: action.session,
        // An address carries no scope, so a restored pane has none to inherit.
        paneScopes: defaultState.paneScopes,
        planEditorReturnTo: null,
        record: action.record,
        mode: action.mode,
        scoutingSession:
          action.activeNoteId === null
            ? base.scoutingSession
            : {
                ...(base.scoutingSession ?? emptyScoutingSession),
                activeNoteId: action.activeNoteId
              }
      })
    }
  }
}

const setPaneLayer = (prev: MapState, pane: Pane, target: PaneTarget): MapState =>
  pane === Pane.Main ? setMainLayer(prev, target) : setComparisonLayer(prev, target)

const setMainLayer = (prev: MapState, { layer, scope }: PaneTarget): MapState => {
  const session: InField =
    prev.session.kind === 'InField'
      ? { ...prev.session, mainMap: layer }
      : { kind: 'InField', mainMap: layer, comparisonMap: null }

  // The editor's cancel target only survives while an editor layer is active.
  const planEditorReturnTo = isEditorLayer(layer) ? prev.planEditorReturnTo : null

  const identityHeld =
    prev.session.kind === 'InField' && layersHaveSameIdentity(prev.session.mainMap, layer)

  const base = identityHeld ? prev : clearMainScopedState(prev)
  return disarmPassEdit(withPaneScope({ ...base, session, planEditorReturnTo }, Pane.Main, scope))
}

const setComparisonLayer = (prev: MapState, { layer, scope }: PaneTarget): MapState => {
  if (prev.session.kind !== 'InField') return prev
  // The comparison pane can't host an editor (main-pane-only state).
  if (isEditorLayer(layer)) return prev
  const disabled = prev.session.comparisonMap?.disabled ?? false
  return disarmPassEdit(
    withPaneScope(
      { ...prev, session: { ...prev.session, comparisonMap: { selection: layer, disabled } } },
      Pane.Comparison,
      scope
    )
  )
}

const withPaneScope = (state: MapState, pane: Pane, scope: PaneScope | null): MapState => ({
  ...state,
  paneScopes: { ...state.paneScopes, [pane]: scope }
})

/**
 * State whose meaning is tied to the main pane's stream: the scrubber and its
 * history, the four per-mode sessions, and the filter list.
 *
 * Filters belong here even though they name their own streams, because the
 * editor offers every stream in the main pane's crop cycle — so a filter can
 * target one no pane displays, and only a move out of that crop cycle orphans
 * the set. Clearing on any stream change is therefore blunter than it needs to
 * be: deciding precisely would take the stream list, which is catalog data this
 * reducer deliberately cannot reach.
 */
const clearMainScopedState = (state: MapState): MapState => ({
  ...state,
  dataTools: { ...state.dataTools, filters: [] },
  record: LATEST_DATA,
  recordSelections: null,
  diagnosticsSession: null,
  configSession: null,
  scoutingSession: null,
  streamEditSession: null
})

/**
 * An armed pass-edit row points at one layer; once the pane moves off it there
 * is no editor for it to belong to.
 */
const disarmPassEdit = (state: MapState): MapState =>
  state.dataTools.passEdit === null
    ? state
    : { ...state, dataTools: { ...state.dataTools, passEdit: null } }

/**
 * The data-tools state a mode or tab change leaves behind: in-flight work is
 * dropped, committed work is kept, and the lasso's activation is derived from
 * the incoming pair rather than corrected afterwards by whatever is mounted.
 */
const idleDataTools = ({
  state,
  mode,
  detailsTab
}: {
  state: MapState
  mode: MapMode
  detailsTab: DetailsTab
}) => ({
  ...state.dataTools,
  lassoActive: mode === MapMode.Details && detailsTab === DetailsTab.Tools,
  lassoDrawing: false,
  passEdit: null
})
